// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PacketGenerator } from "@/components/admin-dashboard/PacketGenerator";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { 
  Download,
  Users,
  Trophy,
  Calendar,
  Zap,
  Loader2,
  Eye,
  EyeOff
} from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Template, TeamsSummary } from "@/types/packet-management";
import { parseLocalDate } from '@/utils/formatters';

interface RealTeamData {
  id: string;
  name: string;
  head_coach_email: string;
  head_coach_name: string;
  age_group: string;
  sport_id: string;
  gender: string;
  playerCount: number;
}

interface TeamDebugInfo {
  step: string;
  status: 'pending' | 'completed' | 'error';
  count?: number;
  data?: any;
  error?: string;
  query?: string;
  eventId?: string;
  timing?: number;
  fullError?: any;
}

interface PacketGeneratorTabProps {
  events: any[];
  selectedEvent: string;
  setSelectedEvent: (eventId: string) => void;
  loadingEvents: boolean;
  teams: any[];
  loadingTeams: boolean;
  selectedAgeGroup: string;
  setSelectedAgeGroup: (ageGroup: string) => void;
  eventAgeGroups: string[];
  teamsSummary: TeamsSummary;
  templates: Template[];
  selectedPacketTemplate: string;
  setSelectedPacketTemplate: (templateId: string) => void;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  generatedPackets: {[key: string]: string};
  setGeneratedPackets: (packets: {[key: string]: string}) => void;
  getFilteredTeams: () => any[];
  generatePacketsForAllTeams: () => void;
  downloadAllPackets: () => void;
  setPurchasablePacket?: (eventId: string, pdfBlob: Blob, filename: string) => Promise<void>;
  generatedPdfBlob?: Blob | null;
  generatedFilename?: string;
}

export const PacketGeneratorTab: React.FC<PacketGeneratorTabProps> = ({
  events,
  selectedEvent,
  setSelectedEvent,
  loadingEvents,
  teams,
  loadingTeams,
  selectedAgeGroup,
  setSelectedAgeGroup,
  eventAgeGroups,
  teamsSummary,
  templates,
  selectedPacketTemplate,
  setSelectedPacketTemplate,
  isGenerating,
  setIsGenerating,
  generatedPackets,
  setGeneratedPackets,
  getFilteredTeams,
  generatePacketsForAllTeams,
  downloadAllPackets,
  setPurchasablePacket,
  generatedPdfBlob: externalGeneratedPdfBlob,
  generatedFilename: externalGeneratedFilename
}) => {
  const [showDebug, setShowDebug] = useState(true);
  const [teamDebugInfo, setTeamDebugInfo] = useState<{[teamId: string]: TeamDebugInfo[]}>({});
  const [loadingDebugInfo, setLoadingDebugInfo] = useState<{[teamId: string]: boolean}>({});
  const [realTeams, setRealTeams] = useState<RealTeamData[]>([]);
  const [loadingRealTeams, setLoadingRealTeams] = useState(false);
  const { toast } = useToast();

  // Use external state if provided, otherwise use internal state
  const generatedPdfBlob = externalGeneratedPdfBlob;
  const generatedFilename = externalGeneratedFilename || '';

  const handleSetPurchasablePacket = async () => {
    if (!selectedEvent || !generatedPdfBlob || !generatedFilename || !setPurchasablePacket) {
      toast({
        title: "Error",
        description: "No PDF available to set as purchasable packet",
        variant: "destructive"
      });
      return;
    }

    await setPurchasablePacket(selectedEvent, generatedPdfBlob, generatedFilename);
  };

  // Use teams prop from parent (already correctly populated from both old and new registration systems)
  // The parent PacketManagementPage.fetchEventTeams handles querying both systems
  useEffect(() => {
    if (teams && teams.length > 0) {
      console.log('✅ Using teams from parent prop:', teams.length, 'teams');
      // Map teams prop to realTeams format for compatibility
      const mappedTeams: RealTeamData[] = teams.map(team => ({
        id: team.team_id || team.id,
        name: team.name,
        head_coach_email: team.coach_email || '',
        head_coach_name: team.coach_name || 'Unknown Coach',
        age_group: team.age_group || 'Mixed',
        sport_id: team.sport_id || '',
        gender: team.gender || 'Mixed',
        playerCount: team.roster_count || 0
      }));
      setRealTeams(mappedTeams);
      setLoadingRealTeams(false);
    } else if (selectedEvent) {
      setRealTeams([]);
      setLoadingRealTeams(false);
    }
  }, [teams, selectedEvent]);

  // Function to fetch debug info for a specific REAL team
  const fetchTeamDebugInfo = async (teamId: string) => {
    if (!selectedEvent) {
      console.log('Debug: No selectedEvent provided');
      return;
    }
    
    const realTeam = realTeams.find(t => t.id === teamId);
    if (!realTeam) {
      console.log('Debug: No real team found for ID:', teamId);
      return;
    }
    
    console.log(`Debug: Starting fetchTeamDebugInfo for REAL team: ${realTeam.name} (${teamId})`);
    
    setLoadingDebugInfo(prev => ({ ...prev, [teamId]: true }));
    setTeamDebugInfo(prev => ({ ...prev, [teamId]: [] }));
    
    const debugSteps: TeamDebugInfo[] = [];
    const startTime = performance.now();
    
    try {
      // Step 1: Team is already confirmed registered (we got it from registrations query)
      const step1StartTime = performance.now();
      
      debugSteps.push({ 
        step: `Step 1: Team verified registered: ${realTeam.name}`, 
        status: 'completed',
        eventId: selectedEvent,
        count: 1,
        data: { coach_email: realTeam.head_coach_email, coach_name: realTeam.head_coach_name },
        timing: performance.now() - step1StartTime
      });
      setTeamDebugInfo(prev => ({ ...prev, [teamId]: [...debugSteps] }));

      // Step 2: Get team roster details
      console.log('Step 2: Getting team roster details for team:', realTeam.name);
      const step2StartTime = performance.now();
      
      debugSteps.push({ 
        step: `Step 2: Get roster for team: ${realTeam.name}`, 
        status: 'pending',
        query: `from('coach_team_rosters').select('*, profiles:profile_id(*)').eq('team_id', '${teamId}')`
      });
      setTeamDebugInfo(prev => ({ ...prev, [teamId]: [...debugSteps] }));
      
      const { data: rosters, error: rostersError } = await supabase
        .from('coach_team_rosters')
        .select(`
          *,
          profiles:profile_id (
            status,
            user_id,
            username,
            name,
            role
          )
        `)
        .eq('team_id', teamId);

      const step2Time = performance.now() - step2StartTime;
      
      if (rostersError) {
        debugSteps[debugSteps.length - 1] = { 
          step: `Step 2: Team roster query FAILED`, 
          status: 'error', 
          error: `ERROR: ${rostersError.message}`,
          fullError: rostersError,
          timing: step2Time,
          query: `from('coach_team_rosters').select('*, profiles:profile_id(*)').eq('team_id', '${teamId}')`
        };
        setTeamDebugInfo(prev => ({ ...prev, [teamId]: [...debugSteps] }));
        return;
      }

      const playerCount = rosters?.length || 0;
      debugSteps[debugSteps.length - 1] = { 
        step: `Step 2: Found ${playerCount} players for team: ${realTeam.name}`, 
        status: 'completed', 
        count: playerCount, 
        data: rosters?.slice(0, 3),
        timing: step2Time,
        query: `from('coach_team_rosters').select('*, profiles:profile_id(*)').eq('team_id', '${teamId}')`
      };
      
      // Step 3: Final summary
      const totalTime = performance.now() - startTime;
      debugSteps.push({
        step: `FINAL RESULT: Team "${realTeam.name}" has ${playerCount} players`,
        status: playerCount > 0 ? 'completed' : 'error',
        count: playerCount,
        data: {
          teamName: realTeam.name,
          teamId: teamId,
          coachEmail: realTeam.head_coach_email,
          coachName: realTeam.head_coach_name,
          totalPlayers: playerCount,
          totalQueryTime: `${totalTime.toFixed(2)}ms`
        },
        timing: totalTime,
        error: playerCount > 0 ? undefined : 'No players found for this team'
      });

      setTeamDebugInfo(prev => ({ ...prev, [teamId]: [...debugSteps] }));

    } catch (error: any) {
      console.error('Critical error in fetchTeamDebugInfo:', error);
      debugSteps.push({ 
        step: 'CRITICAL ERROR', 
        status: 'error', 
        error: `EXCEPTION: ${error?.message || 'Unknown error'}`,
        fullError: error,
        data: { errorStack: error?.stack }
      });
      setTeamDebugInfo(prev => ({ ...prev, [teamId]: [...debugSteps] }));
    } finally {
      setLoadingDebugInfo(prev => ({ ...prev, [teamId]: false }));
      const totalTime = performance.now() - startTime;
      console.log(`Total debug time for team ${teamId}: ${totalTime.toFixed(2)}ms`);
    }
  };

  // Filter real teams by age group
  const getFilteredRealTeams = (): RealTeamData[] => {
    if (selectedAgeGroup === 'all' || !selectedAgeGroup) {
      return realTeams;
    }
    return realTeams.filter(team => team.age_group === selectedAgeGroup);
  };

  // NOTE: Teams are now populated from parent prop via the useEffect above

  // Auto-fetch debug info when real teams change
  useEffect(() => {
    if (selectedEvent && realTeams.length > 0) {
      const filteredTeams = getFilteredRealTeams();
      filteredTeams.forEach(team => {
        fetchTeamDebugInfo(team.id);
      });
    }
  }, [selectedEvent, realTeams, selectedAgeGroup]);

  // Calculate real team summary
  const realTeamsSummary = {
    totalTeams: realTeams.length,
    ageGroupCount: [...new Set(realTeams.map(t => t.age_group))].length,
    totalPlayers: realTeams.reduce((sum, team) => sum + team.playerCount, 0)
  };

  return (
    <div className="space-y-6">
      {/* Fixed Teams Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
        <p className="text-green-800 text-sm font-medium">✅ FIXED: Now showing REAL team data from coach_teams table instead of form responses</p>
      </div>

      {/* Event and Template Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Event
            </CardTitle>
            <CardDescription>
              Choose the event to generate packets for
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {loadingEvents ? (
                  <SelectItem value="" disabled>
                    Loading events...
                  </SelectItem>
                ) : events.length === 0 ? (
                  <SelectItem value="" disabled>
                    No events available
                  </SelectItem>
                ) : (
                  events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} ({parseLocalDate(event.start_date).toLocaleDateString()})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Template Selection
            </CardTitle>
            <CardDescription>
              Choose the packet template to use
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedPacketTemplate} onValueChange={setSelectedPacketTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <SelectItem value="" disabled>
                    No templates available
                  </SelectItem>
                ) : (
                  templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Generation Actions */}
      {selectedEvent && selectedPacketTemplate && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Generate Packets
            </CardTitle>
            <CardDescription>
              Generate roster packets for all teams
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Age Group Filter */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium">Filter by Age Group:</label>
                  <Select value={selectedAgeGroup} onValueChange={setSelectedAgeGroup}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="All age groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Age Groups</SelectItem>
                      {[...new Set(realTeams.map(t => t.age_group))].map((ageGroup) => (
                        <SelectItem key={ageGroup} value={ageGroup}>
                          {ageGroup}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Real Team Summary Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{realTeamsSummary.totalTeams}</div>
                    <div className="text-xs text-muted-foreground">Teams</div>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{realTeamsSummary.ageGroupCount}</div>
                    <div className="text-xs text-muted-foreground">Age Groups</div>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{realTeamsSummary.totalPlayers}</div>
                    <div className="text-xs text-muted-foreground">Players</div>
                  </div>
                </div>
              </div>

              {/* Generation Buttons */}
              <div className="flex flex-col gap-3">
                <div className="flex gap-4">
                  <Button
                    onClick={downloadAllPackets}
                    disabled={isGenerating || !selectedEvent || !selectedPacketTemplate || getFilteredRealTeams().length === 0}
                    className="flex-1"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF Packet ({getFilteredRealTeams().length} teams)
                      </>
                    )}
                  </Button>

                  {generatedPdfBlob && generatedFilename && setPurchasablePacket && (
                    <Button
                      variant="secondary"
                      onClick={handleSetPurchasablePacket}
                      className="flex-shrink-0"
                    >
                      Set as Purchasable Packet
                    </Button>
                  )}
                </div>

                {/* Combined Download & Set Purchasable Button */}
                <Button
                  onClick={async () => {
                    if (!selectedEvent || !setPurchasablePacket) {
                      toast({
                        title: "Error",
                        description: "Please select an event first",
                        variant: "destructive"
                      });
                      return;
                    }

                    console.log('🚀 Starting combined download & set purchasable...');
                    
                    try {
                      // First download the packets (this generates the PDF)
                      await downloadAllPackets();
                      
                      // Poll for the blob with multiple retries
                      const pollForBlob = async (attempt = 1, maxAttempts = 6) => {
                        console.log(`🔍 Checking for blob (attempt ${attempt}/${maxAttempts})...`, { 
                          externalGeneratedPdfBlob: !!externalGeneratedPdfBlob, 
                          externalGeneratedFilename: !!externalGeneratedFilename 
                        });
                        
                        if (externalGeneratedPdfBlob && externalGeneratedFilename) {
                          console.log('📦 Found generated blob, setting as purchasable...');
                          await handleSetPurchasablePacket();
                          return;
                        }
                        
                        if (attempt < maxAttempts) {
                          console.log(`⏳ Blob not ready yet, waiting 1 second before retry ${attempt + 1}...`);
                          setTimeout(() => pollForBlob(attempt + 1, maxAttempts), 1000);
                        } else {
                          console.log('❌ Max attempts reached, blob still not found');
                          toast({
                            title: "Warning", 
                            description: "PDF downloaded but could not set as purchasable packet. Please try again.",
                            variant: "destructive"
                          });
                        }
                      };
                      
                      // Start polling after 1.5 seconds
                      setTimeout(() => pollForBlob(), 1500);
                    } catch (error) {
                      console.error('❌ Error in combined operation:', error);
                      toast({
                        title: "Error",
                        description: "Failed to complete operation",
                        variant: "destructive"
                      });
                    }
                  }}
                  disabled={isGenerating || !selectedEvent || !selectedPacketTemplate || getFilteredRealTeams().length === 0}
                  variant="default"
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download & Set as Purchasable ({getFilteredRealTeams().length} teams)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Real Teams Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Registered Teams
          </CardTitle>
          <CardDescription>
            Teams with registrations for this event
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedEvent ? (
            <div className="space-y-4">
              {loadingRealTeams ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Loading Real Teams</h3>
                  <p className="text-muted-foreground">Fetching actual team data...</p>
                </div>
              ) : realTeams.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No teams found</h3>
                  <p>No coach teams have registrations for this event yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                  {getFilteredRealTeams().map((team) => (
                    <Card key={team.id} className="p-4 border-green-200">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-green-800">{team.name}</h4>
                          <Badge variant="outline" className="text-xs bg-green-50">
                            {team.age_group}
                          </Badge>
                        </div>
                         <div className="text-sm text-muted-foreground space-y-1">
                           <p>Coach: {team.head_coach_name}</p>
                           <p>Email: {team.head_coach_email}</p>
                           <p>Players: {team.playerCount}</p>
                           <p>Gender: {team.gender}</p>
                         </div>

                        {selectedPacketTemplate && (
                          <div className="pt-2 border-t">
                            <PacketGenerator
                              eventId={selectedEvent}
                              eventName={events.find(e => e.id === selectedEvent)?.name || 'Event'}
                              teamId={team.id}
                              teamName={team.name}
                              templateData={templates.find(t => t.id === selectedPacketTemplate)}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">Select an Event</h3>
              <p>Choose an event to generate packets for registered teams</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};